import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ExternalLink, User, Users, Settings, LifeBuoy, LogOut, Check } from 'lucide-react';
import { Link, useNavigate } from "react-router-dom";

export default function AccountDropdown({ user, logout, userTeams = [], selectedTeam, onTeamChange }) {
    const navigate = useNavigate();

    if (!user) return null;

    const { name, email } = user;
    const initial = (name || email || '?').charAt(0).toUpperCase();

    // Filter to show only teams where user is NOT the owner (shared teams)
    const memberTeams = userTeams.filter(t => t.role !== 'owner');

    const handleTeamSelect = (teamValue) => {
        if (onTeamChange) {
            onTeamChange(teamValue);
        }
        // Navigate to dashboard with team filter
        navigate(`/dashboard?team=${teamValue}`);
    };

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button className="account-trigger-btn">
                    <span className="account-trigger-btn__avatar">{initial}</span>
                    <span className="account-trigger-btn__name">{name}</span>
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content
                sideOffset={10}
                align="end"
                className="account-dropdown"
            >
                {/* Signed in info — header card */}
                <div className="account-dropdown__profile">
                    <div className="account-dropdown__profile-avatar">{initial}</div>
                    <div className="account-dropdown__profile-text">
                        <div className="account-dropdown__profile-name">{name}</div>
                        <div className="account-dropdown__profile-email">{email}</div>
                    </div>
                </div>

                {/* Teams section */}
                <DropdownMenu.Label className="account-dropdown__label">
                    Teams
                </DropdownMenu.Label>

                {/* My Personal Sites */}
                <DropdownMenu.Item
                    className={`account-dropdown__item ${selectedTeam === 'personal' ? 'account-dropdown__item--active' : ''}`}
                    onClick={() => handleTeamSelect('personal')}
                    data-testid="dropdown-team-personal"
                >
                    <span className="account-dropdown__item-icon">
                        <User className="w-4 h-4" />
                    </span>
                    <span className="account-dropdown__item-label">My Personal Sites</span>
                    {selectedTeam === 'personal' && (
                        <Check className="w-4 h-4 account-dropdown__check" />
                    )}
                </DropdownMenu.Item>

                {/* Team options */}
                {memberTeams.map(team => (
                    <DropdownMenu.Item
                        key={team.id}
                        className={`account-dropdown__item ${selectedTeam === team.id ? 'account-dropdown__item--active' : ''}`}
                        onClick={() => handleTeamSelect(team.id)}
                        data-testid={`dropdown-team-${team.id}`}
                    >
                        <span className="account-dropdown__item-icon">
                            <Users className="w-4 h-4" />
                        </span>
                        <div className="account-dropdown__item-stack">
                            <span className="account-dropdown__item-label">{team.name}</span>
                            <span className="account-dropdown__item-sub">{team.role}</span>
                        </div>
                        {selectedTeam === team.id && (
                            <Check className="w-4 h-4 account-dropdown__check" />
                        )}
                    </DropdownMenu.Item>
                ))}

                <DropdownMenu.Separator className="account-dropdown__separator" />

                <DropdownMenu.Item className="account-dropdown__item" asChild>
                    <Link to="/account">
                        <span className="account-dropdown__item-icon">
                            <Settings className="w-4 h-4" />
                        </span>
                        <span className="account-dropdown__item-label">Account settings</span>
                    </Link>
                </DropdownMenu.Item>

                <DropdownMenu.Item className="account-dropdown__item" asChild>
                    <a href="mailto:support@example.com" target="_blank" rel="noopener noreferrer">
                        <span className="account-dropdown__item-icon">
                            <LifeBuoy className="w-4 h-4" />
                        </span>
                        <span className="account-dropdown__item-label">Contact support</span>
                        <ExternalLink className="w-3.5 h-3.5 account-dropdown__external" />
                    </a>
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="account-dropdown__separator" />

                <DropdownMenu.Item
                    className="account-dropdown__item account-dropdown__item--danger"
                    onClick={logout}
                >
                    <span className="account-dropdown__item-icon">
                        <LogOut className="w-4 h-4" />
                    </span>
                    <span className="account-dropdown__item-label">Log out</span>
                </DropdownMenu.Item>
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
}
